import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import Person from '@mui/icons-material/Person'
import Search from '@mui/icons-material/Search'
import Security from '@mui/icons-material/Security'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import GroupIcon from '@mui/icons-material/Group'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { styled } from '@mui/material/styles'
import {
  getDataMapFromCommonResponse,
  getImageLink,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { toast } from 'react-toastify'
import { MENTOR_MENU_ITEMS } from 'src/supportPortal/components/common/mentor/listing/utils'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getSPDataWithParams, getSPDataWithoutParams } from 'src/supportPortal/services/services'
import { updateSupportPractitionerEnabled } from 'src/supportPortal/services/practitionerOnboardingService'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import ExpandMore from '@mui/icons-material/ExpandMore'
import ProfileDrawer from 'src/supportPortal/components/profile/ProfileDrawer'
import SupportContactActions from 'src/supportPortal/components/contact/SupportContactActions'
import ProfileViewer from 'src/mentor/components/MentorProfilePage/components/ProfileViewer'
import ScreenerProfileViewer from 'src/supportPortal/components/profile/ScreenerProfileViewer'
import useServerAuth from 'src/customHooks/useServerAuth'

// Phase 14x — humanise approved-discipline slugs for the chips rendered below practitioner
// names. Falls back to a sentence-cased version of the slug for any new discipline value
// the support / onboarding flows introduce later.
function formatDisciplineLabel(slug) {
  if (!slug) return ''
  const known = {
    psychotherapy: 'Psychotherapy',
    'nutrition-consultation': 'Nutrition Consultation',
  }
  if (known[slug]) return known[slug]
  return slug
    .split('-')
    .map((part) => (part ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ')
}

const TherapistCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: '1400px',
  borderRadius: '16px',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '1px solid rgba(0, 0, 0, 0.06)',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12), 0 2px 6px rgba(0, 0, 0, 0.08)',
    transform: 'translateY(-2px)',
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '12px',
  },
}))

const ProfileSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  cursor: 'pointer',
  padding: theme.spacing(1),
  borderRadius: '12px',
  transition: 'all 0.2s ease',
  flex: 1,
  minWidth: 0,
  '&:hover': {
    backgroundColor: 'rgba(216, 101, 0, 0.04)',
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
    padding: theme.spacing(0.75),
  },
}))

const ActionButtonsContainer = styled(Stack)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.75),
    width: '100%',
    marginTop: theme.spacing(1),
  },
}))

const StyledButton = styled(Button)(({ theme }) => ({
  fontSize: '0.75rem',
  fontWeight: 500,
  textTransform: 'none',
  borderRadius: '8px',
  padding: theme.spacing(0.5, 1.25),
  minWidth: 'auto',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.6875rem',
    padding: theme.spacing(0.4, 1),
    flex: '1 1 auto',
    minWidth: '80px',
  },
}))

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(3, 2),
  background: 'linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%)',
  minHeight: 'calc(100vh - 200px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1.5),
  },
}))

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
  width: '100%',
}))

const SearchContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1400px',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
  },
}))

const SearchActionsRow = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '1400px',
  margin: '0 auto',
  marginBottom: theme.spacing(3),
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: theme.spacing(1.5),
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr',
  },
}))

const SearchTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)',
    },
    '&.Mui-focused': {
      boxShadow: '0 4px 16px rgba(216, 101, 0, 0.15), 0 2px 6px rgba(216, 101, 0, 0.1)',
      borderColor: 'rgba(216, 101, 0, 0.3)',
    },
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.1)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(216, 101, 0, 0.2)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(216, 101, 0, 0.4)',
    },
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.5, 1.5, 1.5, 0),
    fontSize: '0.9375rem',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1.25, 1.25, 1.25, 0),
      fontSize: '0.875rem',
    },
  },
}))

const EMPTY_LIST_API_PARAMS = {}

export default function SupportMentorsListing({
  heading = 'Therapists',
  hideHeading = false,
  mentorsListApiSignature = '/api/secure/cc/support/therapists/clinical/list',
  listApiParams = EMPTY_LIST_API_PARAMS,
  variant = 'therapist',
  exculudeMenuItemsList = [],
  screenerNoSpecialtyFilter = false,
  showOnboardingButton = false,
  onboardingPractitionerType = '',
  // Phase 14x — optional client-side filter on TeacherUnEditableFields.approvedTherapyDisciplines.
  // Pass a single slug like 'psychotherapy' or 'nutrition-consultation' to narrow the list down
  // to practitioners with that discipline approved. null / empty disables the filter.
  disciplineFilter = null,
  // Phase 14x — show the practitioner's approved therapy disciplines as small chips below the
  // name. Off by default to keep existing surfaces unchanged; the therapists listing turns
  // it on so the discipline-aware tabs make sense visually.
  showDisciplineChips = false,
}) {
  const navigate = useNavigate()
  const { allRolesInBoolean } = useServerAuth()
  const [therapistList, setAllTherapists] = useState()
  const [loadingTherapists, setLoadingTherapists] = useState()
  const [entityFeeModels, setEntityFeeModels] = useState()
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false)
  const [selectedMentor, setSelectedMentor] = useState(null)
  const [fullProfileData, setFullProfileData] = useState(null)
  const [screenerProfileData, setScreenerProfileData] = useState(null)
  const [allTags, setAllTags] = useState(null)
  const [loadingProfile, setLoadingProfile] = useState(false)
  const [updatingPractitionerStatus, setUpdatingPractitionerStatus] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const isScreener = variant === 'screener'
  // Determine base path based on current location or API signature
  const isDoctors = mentorsListApiSignature.includes('/doctor/')
  const searchPlaceholder = isDoctors
    ? 'Search doctors by name...'
    : isScreener
      ? 'Search screeners by name...'
      : 'Search therapists by name...'
  const selectedMentorTargetRole = isScreener ? 'screener' : isDoctors ? 'doctor' : 'therapist'
  const selectedMentorEventType = isScreener
    ? 'screening'
    : isDoctors
    ? 'consultation'
    : 'therapy-session'
  const selectedPractitionerLabel = isScreener ? 'Screener' : isDoctors ? 'Doctor' : 'Therapist'
  const canViewTherapistPayoutReports =
    !isDoctors && !isScreener && allRolesInBoolean.isTherapistAdmin
  const basePath = isDoctors
    ? '/support-home/practitioner-management/doctors'
    : '/support-home/practitioner-management/therapists'
  const resolvedOnboardingType = onboardingPractitionerType || selectedMentorTargetRole
  const canSeeOnboardingButton =
    showOnboardingButton &&
    (allRolesInBoolean.isAdmin ||
      ((resolvedOnboardingType === 'doctor' &&
        allRolesInBoolean.isClinicSuccessAdmin) ||
        ((resolvedOnboardingType === 'therapist' || resolvedOnboardingType === 'screener') &&
          allRolesInBoolean.isTherapistAdmin)))
  const onboardingButtonLabel =
    resolvedOnboardingType === 'doctor'
      ? 'Add New Doctor'
      : resolvedOnboardingType === 'screener'
        ? 'Add New Screener'
        : 'Add New Therapist'
  
  // Check if user has access to prescription templates (only for doctors)
  const hasPrescriptionTemplatesAccess = isDoctors && (
    allRolesInBoolean.isClinicSuccessAdmin || 
    allRolesInBoolean.isClinicSuccessManager
  )
  const selectedPractitionerIsEnabled = useMemo(() => {
    if (!fullProfileData && !selectedMentor) {
      return true
    }
    if (fullProfileData?.enabled === false || selectedMentor?.enabled === false) {
      return false
    }
    if (fullProfileData?.services?.live === false || selectedMentor?.services?.live === false) {
      return false
    }
    return true
  }, [fullProfileData, selectedMentor])
  const canEnableSelectedPractitioner =
    Boolean(selectedMentor?.id) &&
    !selectedPractitionerIsEnabled &&
    (allRolesInBoolean.isAdmin ||
      (selectedMentorTargetRole === 'doctor'
        ? allRolesInBoolean.isClinicSuccessAdmin
        : allRolesInBoolean.isTherapistAdmin))

  // Edit-profile gate. Mirrors the support practitioner-onboarding controller's
  // @PreAuthorize (ROLE_ADMIN / ROLE_THERAPIST_ADMIN / ROLE_CLINIC_SUCCESS_ADMIN) so the
  // button only renders for actors who can actually save changes — and the save endpoint
  // is what writes the audit-log entry, not the open action.
  const canEditPractitionerProfile = Boolean(
    selectedMentor?.id &&
      (allRolesInBoolean.isAdmin ||
        (selectedMentorTargetRole === 'doctor'
          ? allRolesInBoolean.isClinicSuccessAdmin
          : allRolesInBoolean.isTherapistAdmin)),
  )

  const handleEditPractitionerProfile = () => {
    if (!selectedMentor?.id) return
    setProfileDrawerOpen(false)
    navigate(
      `/support-home/practitioner-management/onboarding?type=${encodeURIComponent(
        selectedMentorTargetRole,
      )}&practitioner-id=${encodeURIComponent(selectedMentor.id)}`,
    )
  }

  const allTherapists = useMemo(() => {
    if (therapistList) {
      let list = therapistList

      // Screeners – No specialty: filter to mentors with no target speciality
      if (screenerNoSpecialtyFilter) {
        list = list.filter((m) => {
          const slugs = m?.teacherUnEditableFields?.servicesApproved?.ROLE_SCREENER?.targetSpecialitySlugs
          return !slugs || (Array.isArray(slugs) && slugs.length === 0)
        })
      }

      // Phase 14x — discipline filter. The disciplines live on
      // TeacherUnEditableFields.approvedTherapyDisciplines (already returned by the
      // /list endpoint, see MinimalTeacherResponse). Match defensively in case some
      // legacy practitioners are missing the field — they fall through to "no
      // discipline" and are excluded from a discipline-scoped tab.
      if (disciplineFilter) {
        list = list.filter((m) => {
          const disciplines = m?.teacherUnEditableFields?.approvedTherapyDisciplines
          return Array.isArray(disciplines) && disciplines.includes(disciplineFilter)
        })
      }

      const therapistsWithFeeModels = list.map((therapist) => {
        const foundEntityFeeModels = entityFeeModels?.filter((model) => {
          return model?.entityId === therapist?.id
        })
        return {
          ...therapist,
          entityFeeModels: foundEntityFeeModels || [],
        }
      })

      // Filter by search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim()
        return therapistsWithFeeModels.filter((therapist) =>
          therapist?.name?.toLowerCase().includes(query)
        )
      }

      return therapistsWithFeeModels
    }
    return []
  }, [therapistList, entityFeeModels, searchQuery, screenerNoSpecialtyFilter, disciplineFilter])

  async function getTherapistsList() {
    const resp = await getSPDataWithParams(mentorsListApiSignature, listApiParams)
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const mentorsList = getDataMapFromCommonResponse(resp)?.mentor
        setAllTherapists(mentorsList)
      }
    }
  }

  async function getEntityFeeModelsForMentors() {
    const resp = await getSPDataWithParams('/api/secure/cc/support/entity-fee-model/list', {
      'practitioner-ids': (therapistList?.map((therapist) => therapist?.id) || []).join(','),
    })
    if (resp.status === 200) {
      if (isCommonResponseSuccessfulV2(resp)) {
        const entityFeeModels = getDataMapFromCommonResponse(resp)?.['entity-fee-model']
        setEntityFeeModels(entityFeeModels)
      }
    }
  }

  async function setup() {
    setLoadingTherapists(true)
    await getTherapistsList()
    setLoadingTherapists(false)
  }

  async function entityFeeModelsSetup() {
    setLoadingTherapists(true)
    if (therapistList && !!therapistList?.length) {
      await getEntityFeeModelsForMentors()
    }
    setLoadingTherapists(false)
  }

  // Fetch full profile data and allTags when opening profile drawer
  const fetchFullProfileData = async (mentorId) => {
    setLoadingProfile(true)
    setFullProfileData(null)
    setAllTags(null)
    
    try {
      const profileResp = await getSPDataWithoutParams(
        `/api/secure/cc/support/practitioner/profile?practitioner-id=${mentorId}`,
      )
      if (profileResp?.status === 200) {
        // Handle different response structures
        const profileData =
          profileResp.data?.payload ||
          profileResp.data?.data ||
          profileResp.data ||
          null
        if (profileData) {
          setFullProfileData(profileData)
        }
      }
      
      // Fetch allTags (domains) for category display
      const tagsResp = await getSPDataWithoutParams('/api/secure/cc/support/domain/all/list')
      if (tagsResp?.status === 200) {
        const tagsData = tagsResp.data?.payload || tagsResp.data?.data || tagsResp.data || null
        if (tagsData) {
          setAllTags(Array.isArray(tagsData) ? tagsData : tagsData?.domains || [])
        }
      }
    } catch (error) {
      console.error('Error fetching profile data:', error)
      // On error, still show the basic mentor data from listing
    } finally {
      setLoadingProfile(false)
    }
  }

  const fetchScreenerProfile = async (mentorId) => {
    setScreenerProfileData(null)
    try {
      const resp = await getSPDataWithParams('/api/secure/cc/support/screeners/profiles/by-mentor-id', {
        mentorId,
        includeDisabled: true,
        includeDeleted: false,
      })
      if (resp?.status === 200) {
        const raw = getDataMapFromCommonResponse(resp) || resp?.data?.data || resp?.data
        const data = raw?.screenerProfile ?? raw?.profile ?? raw
        setScreenerProfileData(data && typeof data === 'object' ? data : null)
      }
    } catch (e) {
      console.error('Error fetching screener profile:', e)
    }
  }

  const handleProfileClick = async (mentor) => {
    setSelectedMentor(mentor)
    setProfileDrawerOpen(true)
    setScreenerProfileData(null)
    if (mentor?.id) {
      if (isScreener) {
        await Promise.all([fetchScreenerProfile(mentor.id), fetchFullProfileData(mentor.id)])
      } else {
        await fetchFullProfileData(mentor.id)
      }
    }
  }

  const handleProfileDrawerClose = () => {
    setProfileDrawerOpen(false)
    setSelectedMentor(null)
    setFullProfileData(null)
    setScreenerProfileData(null)
    setAllTags(null)
  }

  const handleEnablePractitioner = async () => {
    if (!selectedMentor?.id) {
      return
    }

    setUpdatingPractitionerStatus(true)
    const response = await updateSupportPractitionerEnabled(
      {
        practitionerId: selectedMentor.id,
        practitionerSlug: selectedMentor.slug,
      },
      true,
    )
    setUpdatingPractitionerStatus(false)

    if (!response.ok) {
      toast.error(response.message || `Unable to enable this ${selectedPractitionerLabel.toLowerCase()}.`)
      return
    }

    const refreshedPractitioner = response.data?.practitioner || {}
    setSelectedMentor((prev) =>
      prev
        ? {
            ...prev,
            ...refreshedPractitioner,
            enabled: true,
            services: {
              ...(prev?.services || {}),
              ...(refreshedPractitioner?.services || {}),
              live: true,
            },
          }
        : prev,
    )
    setFullProfileData((prev) =>
      prev
        ? {
            ...prev,
            ...refreshedPractitioner,
            enabled: true,
            services: {
              ...(prev?.services || {}),
              ...(refreshedPractitioner?.services || {}),
              live: true,
            },
          }
        : refreshedPractitioner,
    )
    setAllTherapists((prev) =>
      Array.isArray(prev)
        ? prev.map((item) =>
            item?.id === selectedMentor.id
              ? {
                  ...item,
                  ...refreshedPractitioner,
                  enabled: true,
                  services: {
                    ...(item?.services || {}),
                    ...(refreshedPractitioner?.services || {}),
                    live: true,
                  },
                }
              : item,
          )
        : prev,
    )
    toast.success(`${selectedPractitionerLabel} enabled successfully.`)
  }

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  function renderMenuItem(itemId, therapistItem) {

    switch (itemId) {
      case MENTOR_MENU_ITEMS.ROSTER.id:
        return (
          <StyledButton
            variant="contained"
            size="small"
            startIcon={<CalendarMonthIcon sx={{ fontSize: '14px' }} />}
            onClick={() => {
              navigate(`${basePath}/${therapistItem?.slug}/roster`)
              sessionStorage.setItem('supportMentorData', JSON.stringify(therapistItem))
            }}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: '#fff',
              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5558e3 0%, #7c4ee8 100%)',
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
              },
            }}
          >
            Roster
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.CLINICS.id:
        return (
          <StyledButton
            variant="outlined"
            size="small"
            onClick={() => {
              navigate(`${basePath}/${therapistItem?.slug}`)
              sessionStorage.setItem('supportMentorData', JSON.stringify(therapistItem))
            }}
          >
            Clinics
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.PAYOUT_RECEIPTS.id:
        return (
          <StyledButton
            variant="outlined"
            size="small"
            onClick={() => {
              navigate(`${basePath}/${therapistItem?.slug}/payout-receipts`)
              sessionStorage.setItem('supportMentorData', JSON.stringify(therapistItem))
            }}
          >
            Payout Receipts
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.CALENDAR.id:
        return (
          <StyledButton
            variant="outlined"
            size="small"
            onClick={() => {
              navigate(`${basePath}/${therapistItem?.slug}/calendar`)
              sessionStorage.setItem('supportMentorData', JSON.stringify(therapistItem))
            }}
          >
            Calendar
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.EVENTS.id:
        return (
          <StyledButton
            variant="outlined"
            size="small"
            onClick={() => {
              navigate(`${basePath}/${therapistItem?.slug}/events?tab=events`)
              sessionStorage.setItem('supportMentorData', JSON.stringify(therapistItem))
            }}
          >
            Events
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.APPOINTMENTS.id:
        return (
          <StyledButton
            variant="outlined"
            size="small"
            onClick={() => {
              navigate(`${basePath}/${therapistItem?.slug}/appointments`)
              sessionStorage.setItem('supportMentorData', JSON.stringify(therapistItem))
            }}
          >
            Appointments
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.ENGAGEMENT_MODELS.id:
        return (
          <StyledButton
            variant="outlined"
            size="small"
            disabled={!therapistItem?.entityFeeModels?.length}
            startIcon={
              therapistItem?.teacherUnEditableFields?.showFeeModels ? (
                <Visibility sx={{ fontSize: '14px' }} />
              ) : (
                <VisibilityOff sx={{ fontSize: '14px' }} />
              )
            }
            color={therapistItem?.teacherUnEditableFields?.showFeeModels ? 'success' : 'primary'}
            onClick={() => {
              navigate(`${basePath}/${therapistItem?.slug}/engagement-models`)
              sessionStorage.setItem('supportMentorData', JSON.stringify(therapistItem))
            }}
          >
            {!isMobile && 'Engagement Models'}
            {isMobile && 'Engagement'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.PAYOUT_REPORTS.id:
        if (!canViewTherapistPayoutReports) {
          return <></>
        }
        return (
          <StyledButton
            variant="outlined"
            size="small"
            disabled={!therapistItem?.entityFeeModels?.length}
            startIcon={
              therapistItem?.teacherUnEditableFields?.showPayoutReports ? (
                <Visibility sx={{ fontSize: '14px' }} />
              ) : (
                <VisibilityOff sx={{ fontSize: '14px' }} />
              )
            }
            onClick={() => {
              navigate(`${basePath}/${therapistItem?.slug}/payout-reports`)
              sessionStorage.setItem('supportMentorData', JSON.stringify(therapistItem))
            }}
            color={
              therapistItem?.teacherUnEditableFields?.showPayoutReports ? 'success' : 'primary'
            }
          >
            {!isMobile && 'Payout Reports'}
            {isMobile && 'Reports'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.NEXT_VISITS.id:
        return (
          <StyledButton
            variant="outlined"
            size="small"
            onClick={() => {
              navigate(`${basePath}/${therapistItem?.slug}/next-visits`)
              sessionStorage.setItem('supportMentorData', JSON.stringify(therapistItem))
            }}
            disabled
          >
            {!isMobile && 'Scheduled Visits'}
            {isMobile && 'Visits'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.EMR_SETTINGS.id:
        return (
          <StyledButton
            variant="outlined"
            size="small"
            onClick={() => {
              navigate(`${basePath}/${therapistItem?.slug}/emr-settings`)
              sessionStorage.setItem('supportMentorData', JSON.stringify(therapistItem))
            }}
          >
            {!isMobile && 'EMR Settings'}
            {isMobile && 'EMR'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.PRESCRIPTION_TEMPLATES.id:
        if (!hasPrescriptionTemplatesAccess) {
          return <></>
        }
        return (
          <StyledButton
            variant="outlined"
            size="small"
            onClick={() => {
              navigate(`${basePath}/${therapistItem?.slug}/prescription-templates`)
              sessionStorage.setItem('supportMentorData', JSON.stringify(therapistItem))
            }}
          >
            {!isMobile && 'Prescription Templates'}
            {isMobile && 'Templates'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.INTERVENTIONS.id:
        // Only show for doctors when clinic success doctor-access roles are present.
        if (
          !isDoctors ||
          (!allRolesInBoolean.isClinicSuccessAdmin && !allRolesInBoolean.isClinicSuccessManager)
        ) {
          return <></>
        }
        return (
          <StyledButton
            variant="outlined"
            size="small"
            onClick={() => {
              navigate(`/support-home/prescription-interventions?triggeringDoctorId=${therapistItem?.id}&triggeringDoctorSlug=${therapistItem?.slug}`)
            }}
          >
            {!isMobile && 'Interventions Requested'}
            {isMobile && 'Interventions'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.AUDIT_REQUESTS.id:
        // Only show for users with audit access (L3, Clinic Success Admin/Manager)
        if (!allRolesInBoolean.isL3TherapySupport && !allRolesInBoolean.isClinicSuccessAdmin && !allRolesInBoolean.isClinicSuccessManager) {
          return <></>
        }
        return (
          <StyledButton
            variant="outlined"
            size="small"
            startIcon={<Security sx={{ fontSize: '14px' }} />}
            onClick={() => {
              navigate(`/support-home/practitioner-management/auditors/audit?auditorMentorIds=${therapistItem?.id}`)
            }}
            sx={{
              borderColor: '#8b5cf6',
              color: '#8b5cf6',
              '&:hover': {
                borderColor: '#7c3aed',
                backgroundColor: 'rgba(139, 92, 246, 0.08)',
              },
            }}
          >
            {!isMobile && 'Audit'}
            {isMobile && 'Audit'}
          </StyledButton>
        )
      case MENTOR_MENU_ITEMS.PATIENTS.id:
        return (
          <StyledButton
            variant="contained"
            size="small"
            startIcon={<GroupIcon sx={{ fontSize: '14px' }} />}
            onClick={() => {
              navigate(`${basePath}/${therapistItem?.slug}/patients`)
              sessionStorage.setItem('supportMentorData', JSON.stringify(therapistItem))
            }}
            sx={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: '#fff',
              boxShadow: '0 2px 8px rgba(240, 147, 251, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                boxShadow: '0 4px 12px rgba(240, 147, 251, 0.4)',
              },
            }}
          >
            Patients
          </StyledButton>
        )
      default:
        return null
    }
  }
  useEffect(() => {
    entityFeeModelsSetup()
  }, [therapistList])

  useEffect(() => {
    setup()
  }, [mentorsListApiSignature, listApiParams])

  return (
    <>
      {!hideHeading && <SupportHeader heading={heading} showBackButton />}
      <Container>
        <SearchActionsRow>
          <SearchContainer sx={{ marginBottom: 0 }}>
          <SearchTextField
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: 'rgba(216, 101, 0, 0.6)' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiInputBase-root': {
                fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              },
            }}
          />
          </SearchContainer>
          {canSeeOnboardingButton && (
            <Button
              variant="contained"
              startIcon={<PersonAddAlt1Icon />}
              onClick={() =>
                navigate(
                  `/support-home/practitioner-management/onboarding?type=${encodeURIComponent(
                    resolvedOnboardingType,
                  )}`,
                )
              }
              sx={{
                minHeight: 52,
                borderRadius: 3,
                px: 2.5,
                whiteSpace: 'nowrap',
              }}
            >
              {onboardingButtonLabel}
            </Button>
          )}
        </SearchActionsRow>
        {loadingTherapists ? (
          <LoadingContainer>
            <CircularProgress size={48} />
          </LoadingContainer>
        ) : (
          <Stack
            spacing={2}
            sx={{
              width: '100%',
              maxWidth: '1400px',
              margin: '0 auto',
            }}
          >
            {allTherapists
              ?.sort((a, b) => a.name?.localeCompare(b.name))
              ?.map((ther) => (
                <TherapistCard key={ther?.id}>
                  <CardContent
                    sx={{
                      padding: { xs: theme.spacing(2), sm: theme.spacing(2.5), md: theme.spacing(3) },
                      '&:last-child': {
                        paddingBottom: { xs: theme.spacing(2), sm: theme.spacing(2.5), md: theme.spacing(3) },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        gap: { xs: 2, sm: 2.5 },
                        width: '100%',
                      }}
                    >
                      <ProfileSection 
                        onClick={() => handleProfileClick(ther)}
                        sx={{
                          flex: { xs: '1 1 auto', sm: '1 1 0%' },
                          minWidth: { sm: '200px', md: '250px' },
                          maxWidth: { sm: '100%', md: '400px' },
                        }}
                      >
                        <Avatar
                          src={ther?.profilePicId ? getImageLink(ther?.profilePicId) : null}
                          sx={{
                            width: { xs: 48, sm: 56, md: 64 },
                            height: { xs: 48, sm: 56, md: 64 },
                            bgcolor: 'rgba(216, 101, 0, 0.1)',
                            border: '2px solid rgba(216, 101, 0, 0.2)',
                            flexShrink: 0,
                          }}
                        >
                          <Person sx={{ fontSize: { xs: 24, sm: 28, md: 32 } }} />
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                              color: 'text.primary',
                              marginBottom: 0.5,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              lineHeight: 1.3,
                            }}
                          >
                            {ther?.name ||
                              ther?.fullName ||
                              ther?.displayName ||
                              (isDoctors ? 'Unknown Doctor' : isScreener ? 'Unknown Screener' : 'Unknown Therapist')}
                          </Typography>
                          {/* Phase 14x — show approved therapy disciplines as small chips
                              below the practitioner's name when the parent surface opts in
                              (showDisciplineChips). Renders the human-readable label when
                              we know it ('psychotherapy' → 'Psychotherapy', 'nutrition-
                              consultation' → 'Nutrition Consultation'); otherwise falls
                              back to the raw slug. */}
                          {showDisciplineChips &&
                            Array.isArray(
                              ther?.teacherUnEditableFields?.approvedTherapyDisciplines,
                            ) &&
                            ther.teacherUnEditableFields.approvedTherapyDisciplines.length > 0 && (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                useFlexGap
                                flexWrap="wrap"
                                sx={{ mt: 0.5 }}
                              >
                                {ther.teacherUnEditableFields.approvedTherapyDisciplines.map(
                                  (slug) => (
                                    <Chip
                                      key={`discipline-${slug}`}
                                      label={formatDisciplineLabel(slug)}
                                      size="small"
                                      sx={{
                                        height: '20px',
                                        fontSize: '0.6875rem',
                                        backgroundColor: 'rgba(216, 101, 0, 0.1)',
                                        color: 'rgba(216, 101, 0, 0.95)',
                                        fontWeight: 500,
                                      }}
                                    />
                                  ),
                                )}
                              </Stack>
                            )}
                          {ther?.entityFeeModels?.length > 0 && (
                            <Chip
                              label={`${ther.entityFeeModels.length} Engagement Model${ther.entityFeeModels.length > 1 ? 's' : ''}`}
                              size="small"
                              sx={{
                                height: '20px',
                                fontSize: '0.6875rem',
                                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                color: 'rgba(16, 185, 129, 0.9)',
                                fontWeight: 500,
                                marginTop: 0.5,
                              }}
                            />
                          )}
                        </Box>
                      </ProfileSection>
                      <ActionButtonsContainer>
                        {(() => {
                          // Create ordered list with ROSTER and APPOINTMENTS first
                          const menuItemsEntries = Object.entries(MENTOR_MENU_ITEMS)
                          const rosterEntry = menuItemsEntries.find(([_key, val]) => val?.id === MENTOR_MENU_ITEMS.ROSTER.id)
                          const appointmentsEntry = menuItemsEntries.find(([_key, val]) => val?.id === MENTOR_MENU_ITEMS.APPOINTMENTS.id)
                          const otherEntries = menuItemsEntries.filter(([_key, val]) =>
                            val?.id !== MENTOR_MENU_ITEMS.ROSTER.id && val?.id !== MENTOR_MENU_ITEMS.APPOINTMENTS.id
                          )
                          
                          // Put ROSTER first, then APPOINTMENTS, then the rest
                          const orderedEntries = [
                            ...(rosterEntry ? [rosterEntry] : []),
                            ...(appointmentsEntry ? [appointmentsEntry] : []),
                            ...otherEntries,
                          ]
                          
                          return orderedEntries
                            .filter(([_key, val]) => !exculudeMenuItemsList.includes(val?.id))
                            .map(([key, val]) => (
                              <Box key={val?.id || key}>
                                {renderMenuItem(val?.id, ther)}
                              </Box>
                            ))
                        })()}
                        <Box>
                          <StyledButton
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              navigate(basePath + "/" + ther?.slug + "/events?tab=packs")
                              sessionStorage.setItem('supportMentorData', JSON.stringify(ther))
                            }}
                          >
                            Bundle Packs
                          </StyledButton>
                        </Box>
                      </ActionButtonsContainer>
                    </Box>
                  </CardContent>
                </TherapistCard>
              ))}
            {allTherapists?.length === 0 && !loadingTherapists && (
              <Box
                sx={{
                  textAlign: 'center',
                  padding: theme.spacing(8),
                  color: 'text.secondary',
                }}
              >
                <Typography variant="h6" sx={{ marginBottom: 1 }}>
                  {isScreener ? 'No screeners found' : 'No therapists found'}
                </Typography>
                <Typography variant="body2">
                  {isScreener
                    ? 'There are no screeners in this category at the moment.'
                    : 'There are no therapists available at the moment.'}
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Container>

      {/* Profile Drawer */}
      <ProfileDrawer
        open={profileDrawerOpen}
        onClose={handleProfileDrawerClose}
        title={
          selectedMentor
            ? `${selectedMentor?.name || selectedMentor?.firstName || 'Practitioner'}${isScreener ? ' (Screener)' : ''} – Profile`
            : 'Profile'
        }
      >
        {selectedMentor?.id ? (
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'rgba(248, 250, 252, 0.9)' }}>
            <Stack spacing={1.5}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    label={selectedPractitionerIsEnabled ? `${selectedPractitionerLabel} Active` : `${selectedPractitionerLabel} Disabled`}
                    color={selectedPractitionerIsEnabled ? 'success' : 'warning'}
                    variant={selectedPractitionerIsEnabled ? 'filled' : 'outlined'}
                  />
                  {!selectedPractitionerIsEnabled && (
                    <Chip size="small" label="Bookings held until re-enabled" variant="outlined" />
                  )}
                </Stack>
                {canEnableSelectedPractitioner && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleEnablePractitioner}
                    disabled={updatingPractitionerStatus}
                    startIcon={
                      updatingPractitionerStatus ? <CircularProgress size={14} color="inherit" /> : null
                    }
                  >
                    {updatingPractitionerStatus ? 'Enabling...' : `Enable ${selectedPractitionerLabel}`}
                  </Button>
                )}
              </Stack>
              <SupportContactActions
                practitionerId={selectedMentor.id}
                targetRole={selectedMentorTargetRole}
                eventType={selectedMentorEventType}
                uiSurface="support-practitioner-profile-drawer"
                title="Practitioner Contact"
              />
            </Stack>
          </Box>
        ) : null}
        {loadingProfile && !screenerProfileData && !isScreener ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <CircularProgress />
          </Box>
        ) : isScreener ? (
          <Box sx={{ pt: 0 }}>
            <Accordion defaultExpanded disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ fontWeight: 600 }}>
                Screener Profile
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {loadingProfile && !screenerProfileData ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <ScreenerProfileViewer profile={screenerProfileData} />
                )}
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded={false} disableGutters sx={{ boxShadow: 'none', '&:before': { display: 'none' }, borderBottom: '1px solid', borderColor: 'divider' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ fontWeight: 600 }}>
                Mentor / Practitioner Profile
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                {loadingProfile && !fullProfileData ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  <ProfileViewer
                    profileData={fullProfileData || selectedMentor}
                    allTags={allTags}
                    onEdit={canEditPractitionerProfile ? handleEditPractitionerProfile : null}
                    readApiWithoutParams={getSPDataWithoutParams}
                    techniqueApiPathBuilder={(techniqueId) => `/api/secure/cc/support/technique/${techniqueId}`}
                    certificateListApiPathBuilder={(profileData) =>
                      `/api/secure/cc/support/practitioner/certificate-record/list/paginated?practitioner-id=${profileData?.id}&page=0&size=100`
                    }
                  />
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        ) : (
          <ProfileViewer
            profileData={fullProfileData || selectedMentor}
            allTags={allTags}
            onEdit={canEditPractitionerProfile ? handleEditPractitionerProfile : null}
            readApiWithoutParams={getSPDataWithoutParams}
            techniqueApiPathBuilder={(techniqueId) => `/api/secure/cc/support/technique/${techniqueId}`}
            certificateListApiPathBuilder={(profileData) =>
              `/api/secure/cc/support/practitioner/certificate-record/list/paginated?practitioner-id=${profileData?.id}&page=0&size=100`
            }
          />
        )}
      </ProfileDrawer>
    </>
  )
}

{
  /* <Button
sx={{ fontSize: '8px', minWidth: 'unset !important' }}
onClick={() => {
  navigate(ther?.slug)
  sessionStorage.setItem('supportMentorData', JSON.stringify(ther))
}}
variant="outlined"
size="small"
>
Clinics
</Button>
<Button
sx={{ fontSize: '8px', minWidth: 'unset !important' }}
onClick={() => {
  navigate(`${ther?.slug}/calendar`)
  sessionStorage.setItem('supportMentorData', JSON.stringify(ther))
}}
variant="outlined"
size="small"
>
Calendar
</Button>
<Button
sx={{ fontSize: '8px', minWidth: 'unset !important' }}
onClick={() => {
  navigate(`${ther?.slug}/events?tab=events`)
  sessionStorage.setItem('supportMentorData', JSON.stringify(ther))
}}
variant="outlined"
size="small"
>
Events
</Button>
<Button
sx={{ fontSize: '8px', minWidth: 'unset !important' }}
onClick={() => {
  navigate(`${ther?.slug}/engagement-models`)
  sessionStorage.setItem('supportMentorData', JSON.stringify(ther))
}}
disabled
variant="outlined"
size="small"
>
Engagement Models
</Button>
<Button
sx={{ fontSize: '8px', minWidth: 'unset !important' }}
onClick={() => {
  navigate(`${ther?.slug}/next-visits`)
  sessionStorage.setItem('supportMentorData', JSON.stringify(ther))
}}
disabled
variant="outlined"
size="small"
>
Scheduled Visits
</Button> */
}
